import {
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@veritrellis/ui";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { apiFetch } from "../../lib/api.js";

export type ApproverGroup = {
  id: string;
  name: string;
  environment: string;
};

type Props = {
  workspaceId: string;
  environment: "sandbox" | "production";
  approverGroupId: string | null | undefined;
  permitTtlOverride: number | null | undefined;
  approvalGroupsEnabled: boolean;
  billingLoaded?: boolean;
  onGroupChange: (id: string | null) => void;
  onTtlChange: (seconds: number | null) => void;
  disabled?: boolean;
};

export const ApprovalBlock = ({
  workspaceId,
  environment,
  approverGroupId,
  permitTtlOverride,
  approvalGroupsEnabled,
  billingLoaded = true,
  onGroupChange,
  onTtlChange,
  disabled = false
}: Props) => {
  const groups = useQuery({
    queryKey: ["approver-groups", workspaceId, environment],
    enabled: workspaceId.length > 0,
    queryFn: async () => {
      const res = await apiFetch(
        `/admin/workspaces/${workspaceId}/approver-groups?environment=${environment}`
      );
      if (!res.ok) {
        throw new Error("approver_groups_failed");
      }
      return (await res.json()) as { groups: ApproverGroup[] };
    }
  });

  const groupData = groups.data?.groups ?? [];
  const selectedGroup =
    approverGroupId != null ? groupData.find((g) => g.id === approverGroupId) : null;
  const groupSelectDisabled =
    disabled || groups.isLoading || (billingLoaded && !approvalGroupsEnabled);

  const handleGroupChange = (value: string) => {
    if (value === "__none__") {
      onGroupChange(null);
      return;
    }
    if (billingLoaded && !approvalGroupsEnabled) {
      return;
    }
    onGroupChange(value);
  };

  return (
    <div className="admin-approval-settings">
      <p className="admin-approval-settings-title">Approval settings</p>

      <div className="admin-form-field admin-form-field-wide">
        <label className="admin-form-field-label" htmlFor="approver-group-select">
          Approver group
        </label>
        <p className="admin-form-field-hint">Who receives the approval request in the inbox</p>
        {billingLoaded && !approvalGroupsEnabled ? (
          <p className="admin-form-field-hint" style={{ color: "var(--muted-foreground)" }}>
            Named groups require Pro or Enterprise. Use “Any approver” below, or{" "}
            <Link to="/settings/billing" className="text-primary underline">
              upgrade your plan
            </Link>
            .{" "}
            <Link to="/settings/approver-groups" className="text-primary underline">
              Manage groups
            </Link>
          </p>
        ) : null}
        {billingLoaded && !approvalGroupsEnabled && approverGroupId != null ? (
          <p className="text-xs text-amber-800" style={{ margin: "0.25rem 0 0" }}>
            This rule still references <strong>{selectedGroup?.name ?? "an approver group"}</strong>
            . Clear the group or upgrade before you can save.
          </p>
        ) : null}
        <div className="admin-form-field-control" id="approver-group-select">
          <Select
            value={
              billingLoaded && !approvalGroupsEnabled ? "__none__" : (approverGroupId ?? "__none__")
            }
            onValueChange={handleGroupChange}
            disabled={groupSelectDisabled}
          >
            <SelectTrigger style={{ height: 36, width: "100%" }}>
              <SelectValue placeholder={groups.isLoading ? "Loading…" : "Select a group"} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">None (any approver)</SelectItem>
              {approvalGroupsEnabled
                ? groupData.map((g) => (
                    <SelectItem key={g.id} value={g.id}>
                      {g.name}
                    </SelectItem>
                  ))
                : null}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="admin-form-field">
        <label className="admin-form-field-label" htmlFor="permit-ttl-override">
          Permit TTL override (seconds)
        </label>
        <p className="admin-form-field-hint">
          Optional. Overrides the workspace default after approval. Leave blank to use the default.
        </p>
        <div className="admin-form-field-control">
          <Input
            id="permit-ttl-override"
            type="number"
            style={{ height: 36, width: "100%" }}
            min={60}
            step={60}
            disabled={disabled}
            placeholder="e.g. 3600"
            value={permitTtlOverride ?? ""}
            onChange={(e) => {
              const raw = e.currentTarget.value;
              const n = Number(raw);
              onTtlChange(raw.length === 0 || Number.isNaN(n) ? null : n);
            }}
          />
        </div>
      </div>
    </div>
  );
};
